import { useState, useEffect, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DollarSign, Plus, Search, Pencil, Trash2, CheckCircle, XCircle, ChevronDown, ChevronRight, FilePlus2, FileStack, Hash } from "lucide-react";
import { InvoiceForm } from "@/components/forms/InvoiceForm";
import { PaymentForm } from "@/components/forms/PaymentForm";
import { InvoiceActionsMenu } from "@/components/accounting/InvoiceActionsMenu";
import { ServerPagination } from "@/components/ui/server-pagination";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { canManageModule } from "@/lib/permissions";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { FeeStructureManager } from "@/components/accounting/FeeStructureManager";
import { FeeStructureList } from "@/components/accounting/FeeStructureList";
import { GenerateInvoicesDialog } from "@/components/accounting/GenerateInvoicesDialog";
import { AssignFeeStructureDialog } from "@/components/accounting/AssignFeeStructureDialog";
import { BulkEditAdjustmentDialog } from "@/components/accounting/BulkEditAdjustmentDialog";

const SIT_STATUS_OPTIONS = [
  { value: "first_sit", label: "First Sit" },
  { value: "repeating_module", label: "Repeat Module" },
  { value: "repeat_semester", label: "Repeat Semester" },
  { value: "supplementary", label: "Supplementary" },
];

const PAYMENT_CATEGORY_OPTIONS = [
  { value: "full_payment", label: "Full payment" },
  { value: "got_scholarship", label: "GoT scholarship" },
  { value: "private_scholarship", label: "Private scholarship" },
  { value: "full_scholarship", label: "Full scholarship" },
  { value: "exempted", label: "Exempted" },
];

const Accounting = () => {
  const { roles } = useAuth();
  const canManage = canManageModule(roles, "accounting");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [semesterFilter, setSemesterFilter] = useState<string>("all");
  const [classFilter, setClassFilter] = useState<string>("all");
  const [expandedStudent, setExpandedStudent] = useState<string | null>(null);
  const [prefillStudentId, setPrefillStudentId] = useState<string | null>(null);
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [paymentFormOpen, setPaymentFormOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<any>(null);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>(null);
  const [structureManagerOpen, setStructureManagerOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("invoices");
  const [generateInvoicesFor, setGenerateInvoicesFor] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [sitStatusFilter, setSitStatusFilter] = useState<string>("all");
  const [paymentCategoryFilter, setPaymentCategoryFilter] = useState<string>("all");
  const [assignFor, setAssignFor] = useState<any[] | null>(null);
  const [bulkAdjustFor, setBulkAdjustFor] = useState<any[] | null>(null);
  const queryClient = useQueryClient();

  // Pagination state per tab
  const [invPage, setInvPage] = useState(1);
  const [invPageSize, setInvPageSize] = useState(25);
  const [payPage, setPayPage] = useState(1);
  const [payPageSize, setPayPageSize] = useState(25);
  const [clrPage, setClrPage] = useState(1);
  const [clrPageSize, setClrPageSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setInvPage(1); setPayPage(1); setClrPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setInvPage(1); }, [semesterFilter, classFilter, sitStatusFilter, paymentCategoryFilter]);

  const CLASS_OPTIONS = ["CMT4","CMT5","CMT6","PST4","PST5","PST6","SW4","SW5","SW6"];

  const { data: rosterData, isLoading: loadingRoster } = useQuery({
    queryKey: ["accounting-student-roster", debouncedSearch, semesterFilter, classFilter, sitStatusFilter, paymentCategoryFilter, invPage, invPageSize],
    queryFn: async () => {
      const from = (invPage - 1) * invPageSize;
      const profileSelect = paymentCategoryFilter !== "all"
        ? "student_accounting_profile!inner(payment_category, fee_category_id)"
        : "student_accounting_profile(payment_category, fee_category_id)";
      let q = supabase
        .from("students")
        .select(`id, student_number, first_name, last_name, class, current_semester, sit_status, financial_clearance, ${profileSelect}`, { count: "exact" })
        .order("first_name");
      if (semesterFilter !== "all") q = q.eq("current_semester", semesterFilter);
      if (classFilter !== "all") q = q.eq("class", classFilter);
      if (sitStatusFilter !== "all") q = q.eq("sit_status", sitStatusFilter);
      if (paymentCategoryFilter !== "all") q = q.eq("student_accounting_profile.payment_category", paymentCategoryFilter);
      if (debouncedSearch) {
        const s = debouncedSearch.replace(/[%,]/g, "");
        q = q.or(`first_name.ilike.%${s}%,last_name.ilike.%${s}%,student_number.ilike.%${s}%`);
      }
      q = q.range(from, from + invPageSize - 1);
      const { data: students, error, count } = await q;
      if (error) throw error;
      const ids = (students ?? []).map(s => s.id);
      const classes = Array.from(new Set((students ?? []).map(s => s.class).filter(Boolean))) as string[];
      const profileFor = (s: any) => Array.isArray(s.student_accounting_profile)
        ? s.student_accounting_profile[0]
        : s.student_accounting_profile;
      const assignedCategoryIds = Array.from(new Set(
        (students ?? []).map((s: any) => profileFor(s)?.fee_category_id).filter(Boolean)
      )) as string[];

      let invoicesByStudent: Record<string, any[]> = {};
      let paidByStudent: Record<string, number> = {};
      let feeByClass: Record<string, { schoolFeeInstallments: any[]; otherCostItems: any[]; schoolFeeTotal: number; otherCostTotal: number; categoryName: string | null; }> = {};
      let feeByStudent: Record<string, { schoolFeeInstallments: any[]; otherCostItems: any[]; schoolFeeTotal: number; otherCostTotal: number; categoryName: string | null; }> = {};

      if (ids.length) {
        const [{ data: invs, error: ie }, { data: pays, error: pe }] = await Promise.all([
          supabase.from("invoices").select("*").in("student_id", ids).order("created_at", { ascending: false }),
          supabase.from("payments").select("student_id, amount").in("student_id", ids),
        ]);
        if (ie) throw ie;
        if (pe) throw pe;
        for (const inv of invs ?? []) {
          (invoicesByStudent[inv.student_id] ||= []).push(inv);
        }
        for (const p of pays ?? []) {
          paidByStudent[p.student_id] = (paidByStudent[p.student_id] ?? 0) + Number(p.amount);
        }
      }

      if (classes.length) {
        const { data: cats, error: ce } = await supabase
          .from("fee_categories")
          .select("id, name, classes, fee_structure_items(id, amount, semester, cost_type_id, fee_cost_types(code, name)), fee_installments(id, installment_no, label, amount, due_date, semester, kind)")
          .or(`classes.ov.{${classes.join(",")}}${assignedCategoryIds.length ? `,id.in.(${assignedCategoryIds.join(",")})` : ""}`);
        if (ce) throw ce;
        const buildFeeSummary = (cat: any) => {
          const allInsts = (cat.fee_installments ?? []).slice().sort((a: any, b: any) => (a.semester + a.installment_no).localeCompare(b.semester + b.installment_no));
          const insts = allInsts.filter((i: any) => (i.kind ?? "fee_admission") === "fee_admission");
          const otherItems = (cat.fee_structure_items ?? []).filter((it: any) => it.fee_cost_types?.code !== "FEE_ADMISSION");
          return {
            schoolFeeInstallments: insts,
            otherCostItems: otherItems,
            schoolFeeTotal: insts.reduce((s: number, i: any) => s + Number(i.amount), 0),
            otherCostTotal: otherItems.reduce((s: number, i: any) => s + Number(i.amount), 0),
            categoryName: cat.name,
          };
        };
        for (const cls of classes) {
          // pick the first matching category for this class
          const cat = (cats ?? []).find((c: any) => (c.classes ?? []).includes(cls));
          if (!cat) {
            feeByClass[cls] = { schoolFeeInstallments: [], otherCostItems: [], schoolFeeTotal: 0, otherCostTotal: 0, categoryName: null };
            continue;
          }
          feeByClass[cls] = buildFeeSummary(cat);
        }
        for (const s of students ?? []) {
          const assignedId = profileFor(s)?.fee_category_id;
          const assignedCat = assignedId ? (cats ?? []).find((c: any) => c.id === assignedId) : null;
          if (assignedCat) feeByStudent[s.id] = buildFeeSummary(assignedCat);
        }
      }

      const rows = (students ?? []).map((s: any) => {
        const invs = invoicesByStudent[s.id] ?? [];
        const profileRow = Array.isArray(s.student_accounting_profile)
          ? s.student_accounting_profile[0]
          : s.student_accounting_profile;
        const payment_category: string | null = profileRow?.payment_category ?? null;
        const schoolFees = invs.filter(i => i.kind === "school_fee");
        const schoolFeeInvoice =
          schoolFees.find(i => !!i.control_number) ?? schoolFees[0] ?? null;
        const otherInvoices = invs.filter(i => i.kind === "other");
        const fee = feeByStudent[s.id] || (s.class && feeByClass[s.class]) || { schoolFeeInstallments: [], otherCostItems: [], schoolFeeTotal: 0, otherCostTotal: 0, categoryName: null };
        const paid = paidByStudent[s.id] ?? 0;
        const discount = Number(schoolFeeInvoice?.discount_amount ?? 0);
        const advance = Number(schoolFeeInvoice?.advance_payment ?? 0);
        const dueOverride = schoolFeeInvoice?.due_amount != null ? Number(schoolFeeInvoice.due_amount) : null;
        const computedDue = Math.max(Number(schoolFeeInvoice?.amount ?? 0) - discount - advance - paid, 0);
        const dueAmount = dueOverride != null ? dueOverride : computedDue;
        let derivedStatus: "none" | "pending_cn" | "invoiced" | "settled" = "none";
        if (!schoolFeeInvoice) derivedStatus = "none";
        else if (!schoolFeeInvoice.control_number) derivedStatus = "pending_cn";
        else if (dueAmount <= 0) derivedStatus = "settled";
        else derivedStatus = "invoiced";
        return {
          ...s,
          invoices: invs,
          schoolFeeInvoice,
          otherInvoices,
          fee,
          paid,
          dueAmount,
          derivedStatus,
          payment_category,
        };
      });
      return { rows, totalCount: count ?? 0 };
    },
  });

  const { data: paymentData, isLoading: loadingPayments } = useQuery({
    queryKey: ["payments", payPage, payPageSize],
    queryFn: async () => {
      const from = (payPage - 1) * payPageSize;
      const { data, error, count } = await supabase
        .from("payments")
        .select("*, students(first_name, last_name, student_number), invoices(description)", { count: "exact" })
        .order("created_at", { ascending: false })
        .range(from, from + payPageSize - 1);
      if (error) throw error;
      return { payments: data ?? [], totalCount: count ?? 0 };
    },
  });

  const { data: clearanceData, isLoading: loadingClearance } = useQuery({
    queryKey: ["students-clearance", clrPage, clrPageSize],
    queryFn: async () => {
      const from = (clrPage - 1) * clrPageSize;
      const { data, error, count } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, financial_clearance", { count: "exact" })
        .order("first_name")
        .range(from, from + clrPageSize - 1);
      if (error) throw error;
      return { students: data ?? [], totalCount: count ?? 0 };
    },
    enabled: canManage,
  });


  const deleteInvoiceMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("invoices").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      toast({ title: "Invoice deleted" });
    },
    onError: (error: any) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const clearanceMutation = useMutation({
    mutationFn: async ({ studentId, cleared }: { studentId: string; cleared: boolean }) => {
      const updateData: any = { financial_clearance: cleared };
      if (cleared) {
        updateData.status = "registered_active";
        updateData.financial_clearance_status = "cleared";
      } else {
        updateData.status = "unregistered_active";
        updateData.financial_clearance_status = "not_cleared";
      }
      const { error } = await supabase.from("students").update(updateData).eq("id", studentId);
      if (error) throw error;
    },
    onSuccess: (_, { cleared }) => {
      queryClient.invalidateQueries({ queryKey: ["students-clearance"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      toast({ title: cleared ? "Student financially cleared & status updated to Registered Active" : "Clearance revoked & status reverted to Unregistered Active" });
    },
    onError: (error: any) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["invoices"] });
    queryClient.invalidateQueries({ queryKey: ["payments"] });
    queryClient.invalidateQueries({ queryKey: ["accounting-student-roster"] });
  };

  const rosterRows = rosterData?.rows ?? [];
  const payments = paymentData?.payments ?? [];
  const studentsWithClearance = clearanceData?.students ?? [];

  const fmtMoney = (n: number) => `TZS ${Math.round(n).toLocaleString()}`;
  const statusBadge = (s: string) => {
    if (s === "settled") return <Badge className="bg-green-600 hover:bg-green-600 text-white">Settled</Badge>;
    if (s === "invoiced") return <Badge className="bg-blue-600 hover:bg-blue-600 text-white">Invoiced</Badge>;
    if (s === "pending_cn") return <Badge className="bg-amber-500 hover:bg-amber-500 text-white">Pending Control #</Badge>;
    return <Badge variant="secondary">No Invoice</Badge>;
  };

  const createCombinedInvoice = async (s: any) => {
    try {
      const fee = s.fee ?? { schoolFeeTotal: 0, otherCostItems: [], schoolFeeInstallments: [], categoryName: null };
      const rows: any[] = [];
      if (fee.schoolFeeTotal > 0 && !s.schoolFeeInvoice) {
        rows.push({
          student_id: s.id,
          amount: fee.schoolFeeTotal,
          description: `Fee & Admission — Installments 1-${fee.schoolFeeInstallments.length}`,
          status: "unpaid",
          kind: "school_fee",
        });
      }
      for (const it of fee.otherCostItems) {
        rows.push({
          student_id: s.id,
          amount: Number(it.amount),
          description: `${it.fee_cost_types?.name ?? "Other"} (Sem ${it.semester})`,
          status: "unpaid",
          kind: "other",
          cost_type_id: it.cost_type_id,
        });
      }
      if (rows.length === 0) {
        toast({ title: "Nothing to invoice", description: "All applicable invoices already exist or no fee structure is defined for this class.", variant: "destructive" });
        return;
      }
      const { error } = await supabase.from("invoices").insert(rows);
      if (error) throw error;
      toast({ title: `${rows.length} invoice${rows.length > 1 ? "s" : ""} created` });
      invalidateAll();
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    }
  };

  const toggleOne = (id: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  };
  const visibleIds = rosterRows.map((r: any) => r.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));
  const toggleAllVisible = (checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleIds.forEach(id => { if (checked) next.add(id); else next.delete(id); });
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (label: string, fn: (s: any) => Promise<void>) => {
    const targets = rosterRows.filter((r: any) => selectedIds.has(r.id));
    if (!targets.length) return;
    setBulkRunning(true);
    let ok = 0, fail = 0;
    for (const s of targets) {
      try { await fn(s); ok++; } catch { fail++; }
    }
    setBulkRunning(false);
    clearSelection();
    invalidateAll();
    toast({ title: `${label} complete`, description: `${ok} succeeded${fail ? `, ${fail} failed` : ""}.`, variant: fail ? "destructive" : "default" });
  };

  const bulkCreateInvoices = () => runBulk("Create invoices", async (s) => { await createCombinedInvoice(s); });
  const bulkGenerateControlNumbers = () => runBulk("Generate control numbers", async (s) => {
    if (!s.schoolFeeInvoice) throw new Error("No school-fee invoice");
    if (s.schoolFeeInvoice.control_number) return;
    const { error } = await supabase.rpc("generate_control_number", { _student_id: s.id });
    if (error) throw error;
  });

  return (
    <AppLayout>
      <div className="space-y-3 sm:space-y-4">
        <div className="page-header">
          <div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2">
              Accounting
              <Badge variant="outline" className="text-[10px] font-mono">build v2</Badge>
            </h1>
            <p className="text-muted-foreground text-sm">Manage fees, invoices, payments, and financial clearance</p>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setActiveTab("fee-structure");
                  setStructureManagerOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />Manage Structure
              </Button>
              <Button variant="outline" onClick={() => setPaymentFormOpen(true)}><Plus className="mr-2 h-4 w-4" />Record Payment</Button>
              <Button onClick={() => { setEditingInvoice(null); setPrefillStudentId(null); setInvoiceFormOpen(true); }}><Plus className="mr-2 h-4 w-4" />Create Invoice</Button>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="invoices">Invoices</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="clearance">Financial Clearance</TabsTrigger>
            <TabsTrigger value="fee-structure">Fee Structure</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search by name or student #..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <Select value={semesterFilter} onValueChange={setSemesterFilter}>
                <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Semester" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Semesters</SelectItem>
                  <SelectItem value="I">Semester I</SelectItem>
                  <SelectItem value="II">Semester II</SelectItem>
                </SelectContent>
              </Select>
              <Select value={classFilter} onValueChange={setClassFilter}>
                <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Class" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {CLASS_OPTIONS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={sitStatusFilter} onValueChange={setSitStatusFilter}>
                <SelectTrigger className="w-full sm:w-[160px]"><SelectValue placeholder="Sit Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sit Status</SelectItem>
                  {SIT_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={paymentCategoryFilter} onValueChange={setPaymentCategoryFilter}>
                <SelectTrigger className="w-full sm:w-[170px]"><SelectValue placeholder="Payment Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Payment Categories</SelectItem>
                  {PAYMENT_CATEGORY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Card>
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-base">Student Invoices Roster</CardTitle>
                    <CardDescription>{rosterData?.totalCount ?? 0} students</CardDescription>
                  </div>
                  {canManage && (
                    <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/40 px-2 py-1.5 text-xs">
                      <span className="font-medium">
                        {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Chagua wanafunzi ↓"}
                      </span>
                      <Button size="sm" variant="outline" disabled={bulkRunning || selectedIds.size === 0} onClick={bulkCreateInvoices}>
                        <FileStack className="h-3 w-3 mr-1" />Create invoices
                      </Button>
                      <Button size="sm" variant="outline" disabled={bulkRunning || selectedIds.size === 0} onClick={bulkGenerateControlNumbers}>
                        <Hash className="h-3 w-3 mr-1" />Generate control #
                      </Button>
                      <Button size="sm" variant="outline" disabled={bulkRunning || selectedIds.size === 0} onClick={() => {
                        const targets = rosterRows.filter((r: any) => selectedIds.has(r.id));
                        if (targets.length) setAssignFor(targets);
                      }}>
                        <FilePlus2 className="h-3 w-3 mr-1" />Assign Fee Structure
                      </Button>
                      <Button size="sm" variant="outline" disabled={bulkRunning || selectedIds.size === 0} onClick={() => {
                        const targets = rosterRows.filter((r: any) => selectedIds.has(r.id));
                        if (targets.length) setBulkAdjustFor(targets);
                      }}>
                        <Pencil className="h-3 w-3 mr-1" />Edit Adjustment
                      </Button>
                      {selectedIds.size > 0 && (
                        <Button size="sm" variant="ghost" disabled={bulkRunning} onClick={clearSelection}>Clear</Button>
                      )}
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="table-responsive">
                {loadingRoster ? (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8">
                          {canManage && (
                            <Checkbox
                              checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                              onCheckedChange={(v) => toggleAllVisible(!!v)}
                              aria-label="Select all on this page"
                            />
                          )}
                        </TableHead>
                        <TableHead className="w-8">#</TableHead>
                        <TableHead>Student #</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Class</TableHead>
                        <TableHead>Fee Category</TableHead>
                        <TableHead>Sit Status</TableHead>
                        <TableHead className="text-right">Fee &amp; Admission</TableHead>
                        <TableHead className="text-right">Other Costs</TableHead>
                        <TableHead>Control #</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rosterRows.length === 0 ? (
                        <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-8">No students match the current filters.</TableCell></TableRow>
                      ) : (
                        rosterRows.map((s: any, idx: number) => {
                          const isOpen = expandedStudent === s.id;
                          const rowNo = (invPage - 1) * invPageSize + idx + 1;
                          const isSelected = selectedIds.has(s.id);
                          return (
                            <Fragment key={s.id}>
                              <TableRow className="cursor-pointer" data-state={isSelected ? "selected" : undefined} onClick={() => setExpandedStudent(isOpen ? null : s.id)}>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {canManage && (
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(v) => toggleOne(s.id, !!v)}
                                      aria-label={`Select ${s.first_name} ${s.last_name}`}
                                    />
                                  )}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-xs tabular-nums">{rowNo}</TableCell>
                                <TableCell className="font-mono text-xs">
                                  <span className="inline-flex items-center gap-1">
                                    {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                    {s.student_number || "—"}
                                  </span>
                                </TableCell>
                                <TableCell className="font-medium">
                                  {s.first_name} {s.last_name}
                                </TableCell>
                                <TableCell>{s.class || "—"}</TableCell>
                                <TableCell>
                                  {s.payment_category ? (
                                    <Badge variant="outline" className="text-[10px]">
                                      {(PAYMENT_CATEGORY_OPTIONS.find(o => o.value === s.payment_category)?.label) ?? s.payment_category}
                                    </Badge>
                                  ) : <span className="text-muted-foreground text-xs">—</span>}
                                </TableCell>
                                <TableCell>
                                  {s.sit_status ? (
                                    <Badge variant="secondary" className="text-[10px]">
                                      {(SIT_STATUS_OPTIONS.find(o => o.value === s.sit_status)?.label) ?? s.sit_status}
                                    </Badge>
                                  ) : <span className="text-muted-foreground text-xs">—</span>}
                                </TableCell>
                                <TableCell className="text-right">{fmtMoney(s.fee?.schoolFeeTotal ?? 0)}</TableCell>
                                <TableCell className="text-right">{fmtMoney(s.fee?.otherCostTotal ?? 0)}</TableCell>
                                <TableCell className="font-mono text-xs">{s.schoolFeeInvoice?.control_number ?? "—"}</TableCell>
                                <TableCell>{statusBadge(s.derivedStatus)}</TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {canManage && (
                                    <div className="flex items-center gap-1">
                                      <Button size="sm" variant="ghost" title="Assign / Change Fee Structure" onClick={() => setAssignFor([s])}>
                                        <FilePlus2 className="h-4 w-4" />
                                      </Button>
                                      <InvoiceActionsMenu
                                        student={s}
                                        onCreateCombined={createCombinedInvoice}
                                        onChanged={invalidateAll}
                                      />
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                              {isOpen && (
                                <TableRow>
                                  <TableCell colSpan={12} className="bg-muted/30">
                                    <Tabs defaultValue="school-fee">
                                      <TabsList>
                                        <TabsTrigger value="school-fee">Fee &amp; Admission</TabsTrigger>
                                        <TabsTrigger value="other-costs">Other Costs</TabsTrigger>
                                      </TabsList>
                                      <TabsContent value="school-fee" className="space-y-2">
                                        {(s.fee?.schoolFeeInstallments ?? []).length === 0 ? (
                                          <div className="text-sm text-muted-foreground py-2">No fee structure defined for {s.class || "this class"}.</div>
                                        ) : (
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>#</TableHead>
                                                <TableHead>Label</TableHead>
                                                <TableHead>Sem</TableHead>
                                                <TableHead>Due</TableHead>
                                                <TableHead className="text-right">Amount</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {(s.fee?.schoolFeeInstallments ?? []).map((i: any) => (
                                                <TableRow key={i.id}>
                                                  <TableCell>{i.installment_no}</TableCell>
                                                  <TableCell>{i.label}</TableCell>
                                                  <TableCell>{i.semester}</TableCell>
                                                  <TableCell>{i.due_date || "—"}</TableCell>
                                                  <TableCell className="text-right">{fmtMoney(Number(i.amount))}</TableCell>
                                                </TableRow>
                                              ))}
                                              <TableRow className="font-semibold">
                                                <TableCell colSpan={4}>Total</TableCell>
                                                <TableCell className="text-right">{fmtMoney(s.fee?.schoolFeeTotal ?? 0)}</TableCell>
                                              </TableRow>
                                            </TableBody>
                                          </Table>
                                        )}
                                        {s.schoolFeeInvoice && (
                                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs pt-2">
                                            <div>Invoice: <span className="font-medium">{fmtMoney(Number(s.schoolFeeInvoice.amount))}</span></div>
                                            <div>Discount: <span className="font-medium">{fmtMoney(Number(s.schoolFeeInvoice.discount_amount ?? 0))}</span></div>
                                            <div>Advance: <span className="font-medium">{fmtMoney(Number(s.schoolFeeInvoice.advance_payment ?? 0))}</span></div>
                                            <div>Due Amount: <span className="font-medium">{fmtMoney(s.dueAmount)}</span></div>
                                          </div>
                                        )}
                                      </TabsContent>
                                      <TabsContent value="other-costs" className="space-y-2">
                                        {(s.fee?.otherCostItems ?? []).length === 0 ? (
                                          <div className="text-sm text-muted-foreground py-2">No other costs defined for {s.class || "this class"}.</div>
                                        ) : (
                                          <Table>
                                            <TableHeader>
                                              <TableRow>
                                                <TableHead>Cost</TableHead>
                                                <TableHead>Sem</TableHead>
                                                <TableHead className="text-right">Amount</TableHead>
                                                <TableHead>Status</TableHead>
                                              </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                              {(s.fee?.otherCostItems ?? []).map((it: any) => {
                                                const inv = s.otherInvoices.find((o: any) => o.cost_type_id === it.cost_type_id);
                                                return (
                                                  <TableRow key={it.id}>
                                                    <TableCell>{it.fee_cost_types?.name ?? "—"}</TableCell>
                                                    <TableCell>{it.semester}</TableCell>
                                                    <TableCell className="text-right">{fmtMoney(Number(it.amount))}</TableCell>
                                                    <TableCell>{inv ? <StatusBadge status={inv.status} /> : <Badge variant="secondary">Not invoiced</Badge>}</TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                              <TableRow className="font-semibold">
                                                <TableCell colSpan={2}>Total</TableCell>
                                                <TableCell className="text-right">{fmtMoney(s.fee?.otherCostTotal ?? 0)}</TableCell>
                                                <TableCell></TableCell>
                                              </TableRow>
                                            </TableBody>
                                          </Table>
                                        )}
                                      </TabsContent>
                                    </Tabs>
                                    {s.invoices.length > 0 && canManage && (
                                      <div className="pt-3 border-t mt-3">
                                        <div className="text-xs font-medium mb-1 text-muted-foreground">Existing invoices</div>
                                        <Table>
                                          <TableHeader>
                                            <TableRow>
                                              <TableHead>Description</TableHead>
                                              <TableHead>Kind</TableHead>
                                              <TableHead className="text-right">Amount</TableHead>
                                              <TableHead>Status</TableHead>
                                              <TableHead>Actions</TableHead>
                                            </TableRow>
                                          </TableHeader>
                                          <TableBody>
                                            {s.invoices.map((inv: any) => (
                                              <TableRow key={inv.id}>
                                                <TableCell>{inv.description || "—"}</TableCell>
                                                <TableCell className="capitalize text-xs">{inv.kind?.replace("_", " ")}</TableCell>
                                                <TableCell className="text-right">{fmtMoney(Number(inv.amount))}</TableCell>
                                                <TableCell><StatusBadge status={inv.status} /></TableCell>
                                                <TableCell>
                                                  <div className="flex gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => { setEditingInvoice(inv); setPrefillStudentId(null); setInvoiceFormOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                                                    <Button variant="ghost" size="sm" onClick={() => setDeleteInvoiceId(inv.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                                                  </div>
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
                <ServerPagination currentPage={invPage} pageSize={invPageSize} totalCount={rosterData?.totalCount ?? 0} onPageChange={setInvPage} onPageSizeChange={setInvPageSize} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment History</CardTitle>
                <CardDescription>{paymentData?.totalCount ?? 0} payments recorded</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPayments ? (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">Loading...</div>
                ) : payments.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-muted-foreground">
                    <div className="text-center"><DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No payments recorded yet</p></div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Student</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Method</TableHead>
                        <TableHead>Reference</TableHead>
                        <TableHead>Invoice</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {payments.map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell className="font-medium">{p.students?.first_name} {p.students?.last_name}</TableCell>
                          <TableCell>${p.amount}</TableCell>
                          <TableCell>{p.payment_date}</TableCell>
                          <TableCell className="capitalize">{p.payment_method?.replace("_", " ")}</TableCell>
                          <TableCell className="font-mono text-sm">{p.reference_number}</TableCell>
                          <TableCell>{p.invoices?.description || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <ServerPagination currentPage={payPage} pageSize={payPageSize} totalCount={paymentData?.totalCount ?? 0} onPageChange={setPayPage} onPageSizeChange={setPayPageSize} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="clearance">
            <Card>
              <CardHeader>
                <CardTitle>Financial Clearance</CardTitle>
                <CardDescription>Manage student financial clearance for registration and exams</CardDescription>
              </CardHeader>
              <CardContent className="table-responsive">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student #</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Clearance Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {studentsWithClearance.map((s: any) => (
                      <TableRow key={s.id}>
                        <TableCell className="font-mono">{s.student_number}</TableCell>
                        <TableCell className="font-medium">{s.first_name} {s.last_name}</TableCell>
                        <TableCell>
                          <Badge variant={s.financial_clearance ? "default" : "secondary"}>
                            {s.financial_clearance ? "Cleared" : "Not Cleared"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {canManage && (
                            s.financial_clearance ? (
                              <Button variant="ghost" size="sm" onClick={() => clearanceMutation.mutate({ studentId: s.id, cleared: false })} disabled={clearanceMutation.isPending}>
                                <XCircle className="mr-1 h-3 w-3" />Revoke
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => clearanceMutation.mutate({ studentId: s.id, cleared: true })} disabled={clearanceMutation.isPending}>
                                <CheckCircle className="mr-1 h-3 w-3" />Clear
                              </Button>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {studentsWithClearance.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">{loadingClearance ? "Loading..." : "No students found"}</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
                <ServerPagination currentPage={clrPage} pageSize={clrPageSize} totalCount={clearanceData?.totalCount ?? 0} onPageChange={setClrPage} onPageSizeChange={setClrPageSize} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="fee-structure" className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold">Fee Structures</h3>
                <p className="text-xs text-muted-foreground">Define student categories, fee components, and installment schedules.</p>
              </div>
              {canManage && (
                <Button onClick={() => setStructureManagerOpen(true)}>
                  <Plus className="mr-2 h-4 w-4" />Manage Structure
                </Button>
              )}
            </div>
            <FeeStructureList
              canManage={canManage}
              onGenerateInvoices={(id) => setGenerateInvoicesFor(id)}
              onEdit={(id) => { setEditingCategoryId(id); setStructureManagerOpen(true); }}
              onDuplicate={async (id) => {
                try {
                  const { data: src, error: e1 } = await supabase
                    .from("fee_categories")
                    .select("*, fee_structure_items(*), fee_installments(*)")
                    .eq("id", id)
                    .single();
                  if (e1 || !src) throw e1 ?? new Error("Source not found");
                  const { data: { user } } = await supabase.auth.getUser();
                  const { data: newCat, error: e2 } = await supabase
                    .from("fee_categories")
                    .insert({
                      name: `${src.name} (Copy)`,
                      student_type: src.student_type,
                      classes: src.classes,
                      academic_year: src.academic_year,
                      notes: src.notes,
                      created_by: user?.id ?? null,
                    })
                    .select()
                    .single();
                  if (e2 || !newCat) throw e2 ?? new Error("Insert failed");
                  try {
                    const items = (src.fee_structure_items ?? []).map((i: any) => ({
                      category_id: newCat.id,
                      cost_type_id: i.cost_type_id,
                      academic_year: i.academic_year,
                      semester: i.semester,
                      amount: i.amount,
                    }));
                    if (items.length) {
                      const { error: e3 } = await supabase.from("fee_structure_items").insert(items);
                      if (e3) throw e3;
                    }
                    const insts = (src.fee_installments ?? []).map((i: any) => ({
                      category_id: newCat.id,
                      semester: i.semester,
                      installment_no: i.installment_no,
                      due_date: i.due_date,
                      amount: i.amount,
                      label: i.label,
                      kind: i.kind,
                      start_date: i.start_date,
                      end_date: i.end_date,
                    }));
                    if (insts.length) {
                      const { error: e4 } = await supabase.from("fee_installments").insert(insts);
                      if (e4) throw e4;
                    }
                  } catch (childErr) {
                    await supabase.from("fee_categories").delete().eq("id", newCat.id);
                    throw childErr;
                  }
                  queryClient.invalidateQueries({ queryKey: ["fee_categories"] });
                  toast({ title: "Fee structure duplicated", description: "Edit the copy and save." });
                  setEditingCategoryId(newCat.id);
                  setStructureManagerOpen(true);
                } catch (err: any) {
                  toast({ title: "Duplicate failed", description: err.message, variant: "destructive" });
                }
              }}
            />
          </TabsContent>
        </Tabs>
      </div>

      <InvoiceForm key={`${editingInvoice?.id ?? "new"}-${prefillStudentId ?? ""}-${invoiceFormOpen}`} open={invoiceFormOpen} onOpenChange={(o) => { setInvoiceFormOpen(o); if (!o) setPrefillStudentId(null); }} invoice={editingInvoice} defaultStudentId={prefillStudentId} onSuccess={invalidateAll} />
      <PaymentForm open={paymentFormOpen} onOpenChange={setPaymentFormOpen} onSuccess={invalidateAll} />
      <FeeStructureManager open={structureManagerOpen} onOpenChange={(o) => { setStructureManagerOpen(o); if (!o) setEditingCategoryId(null); }} editingCategoryId={editingCategoryId} />
      <GenerateInvoicesDialog open={!!generateInvoicesFor} onOpenChange={(o) => !o && setGenerateInvoicesFor(null)} categoryId={generateInvoicesFor} />
      <AssignFeeStructureDialog
        open={!!assignFor}
        onOpenChange={(o) => !o && setAssignFor(null)}
        students={assignFor ?? []}
        onDone={() => { invalidateAll(); clearSelection(); }}
      />
      <BulkEditAdjustmentDialog
        open={!!bulkAdjustFor}
        onOpenChange={(o) => !o && setBulkAdjustFor(null)}
        students={bulkAdjustFor}
        onSaved={() => { invalidateAll(); clearSelection(); }}
      />
      <AlertDialog open={!!deleteInvoiceId} onOpenChange={() => setDeleteInvoiceId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (deleteInvoiceId) { deleteInvoiceMutation.mutate(deleteInvoiceId); setDeleteInvoiceId(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
};

export default Accounting;
